'use client';

import { useCallback, useRef, useState } from 'react';
import type { DocumentationPersistHandler } from '../documents-reports/application-documentation/applicationDocumentation.types';
import {
  APPLICATION_TYPE,
  type ApplicationType,
} from './application-general.model';
import { isApplicationTypeSubmitDisabled } from './application-type-documentation.model';
import { ApplicationTypeApiDocumentationSection } from './ApplicationTypeApiDocumentationSection';
import { ApplicationTypeEditDialog } from './ApplicationTypeEditDialog';
import type { ApiDocumentationSource } from './useStagedApiDocumentationFiles';

export type ApplicationTypeDocumentationEditDialogProps = {
  applicationId: string;
  applicationType: ApplicationType;
  documentation: ApiDocumentationSource;
  isReadOnly?: boolean;
  isUpdating: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: ({ isBackendOnly }: { isBackendOnly: boolean }) => Promise<boolean>;
};

export function ApplicationTypeDocumentationEditDialog({
  applicationId,
  applicationType,
  documentation,
  isReadOnly,
  isUpdating,
  onOpenChange,
  onSave,
}: ApplicationTypeDocumentationEditDialogProps) {
  const hasApiDocumentation = documentation.apiDocumentationFiles.length > 0;
  const documentationPersistRef = useRef<DocumentationPersistHandler | null>(
    null,
  );
  const [stagedApiFileCount, setStagedApiFileCount] = useState(0);
  const hasStagedApiDocumentationChanges = stagedApiFileCount > 0;
  const registerDocumentationPersist = useCallback(
    (handler: DocumentationPersistHandler | null) => {
      documentationPersistRef.current = handler;
    },
    [],
  );
  const handleStagedApiFilesChange = useCallback((files: File[]) => {
    setStagedApiFileCount(files.length);
  }, []);

  const handleSubmit = async (draft: ApplicationType): Promise<boolean> => {
    const isDraftBackendOnly = draft === APPLICATION_TYPE.backendOnly;

    if (isDraftBackendOnly) {
      const persistDocumentation = documentationPersistRef.current;

      if (hasStagedApiDocumentationChanges && !persistDocumentation) {
        return false;
      }

      const didPersistDocumentation = persistDocumentation
        ? await persistDocumentation()
        : true;

      if (!didPersistDocumentation) {
        return false;
      }
    }

    return onSave({ isBackendOnly: isDraftBackendOnly });
  };

  return (
    <ApplicationTypeEditDialog
      open
      applicationType={applicationType}
      loading={isUpdating}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      isSubmitDisabled={(draft) =>
        isApplicationTypeSubmitDisabled({
          draft,
          applicationType,
          hasApiDocumentation,
          hasStagedApiDocumentationChanges,
        })
      }
      renderDraftDetails={(draft) =>
        draft === APPLICATION_TYPE.backendOnly ? (
          <ApplicationTypeApiDocumentationSection
            applicationId={applicationId}
            documentation={documentation}
            isReadOnly={isReadOnly}
            onPersistReady={registerDocumentationPersist}
            onStagedApiFilesChange={handleStagedApiFilesChange}
          />
        ) : null
      }
    />
  );
}
